import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useClient } from '../contexts/ClientContext';
import { FiLock, FiAlertTriangle } from 'react-icons/fi';
import { useCompanyFeatures } from '../contexts/CompanyFeaturesContext';
import { hasFullPermissions } from '../config/permissionsConfig';

interface FeatureGuardProps {
  feature: string;
  children: React.ReactNode;
  fallback?: React.ReactNode | null;
}

const FeatureGuard: React.FC<FeatureGuardProps> = ({ feature, children, fallback }) => {
  const { company } = useParams<{ company: string }>();
  const { client } = useClient();
  const { hasFeature, loading, enabledFeatures, plan } = useCompanyFeatures();
  const [dataLoaded, setDataLoaded] = useState(false);

  // Track if data has been loaded at least once
  // This prevents showing "Access Denied" during initial load or refresh
  useEffect(() => {
    if (!loading) {
      // Once loading is complete, check if we have any data (plan or enabledFeatures)
      // Even if both are null, it means data was loaded (just empty)
      // We set dataLoaded to true after a short delay to ensure state has settled
      const timer = setTimeout(() => {
        setDataLoaded(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, enabledFeatures, plan]);

  if (client?.role === 'superadmin' || !company || company === 'superadmin') {
    return <>{children}</>;
  }

  // Check if manager has full permissions (admin role) - give them full access
  const userPermissions = Array.isArray(client?.permissions) ? (client?.permissions || []) : [];
  const isManagerWithAdminRole = client?.role === 'manager' && hasFullPermissions(userPermissions);
  
  // Managers with full permissions (admin role) get full access like admins
  if (isManagerWithAdminRole) {
    return <>{children}</>;
  }

  // Show loading if still loading OR if data hasn't been confirmed as loaded yet
  // This prevents showing "Access Denied" during initial load or refresh
  if (loading || !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600">טוען...</p>
        </div>
      </div>
    );
  }

  // Use hasFeature which combines plan features with enabled features
  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  if (fallback !== undefined) {
    return <>{fallback}</>;
  }

  return <FeatureAccessDenied feature={feature} />;
};

const FeatureAccessDenied: React.FC<{ feature: string }> = () => {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50" dir="rtl">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <FiLock className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">אין גישה לדף זה</h1>
          <p className="text-gray-600 mb-4">
            התכונה שביקשת לגשת אליה אינה זמינה לחברה שלך.
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <FiAlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-right text-sm text-blue-800">
              <p className="font-semibold mb-1">לצורך גישה לדף זה, אנא פנה לחברת Together</p>
              <p className="text-blue-700">
                נשמח לעזור לך להפעיל את התכונה הנדרשת עבור החברה שלך.
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => window.history.back()}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
        >
          חזור לדף הקודם
        </button>
      </div>
    </div>
  );
};

export default FeatureGuard;

